import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Car, Baby, Dog, Briefcase, Star, Loader2, Plus, Eye, Sticker, CreditCard, ShieldCheck, Clock, PauseCircle } from "lucide-react";
import AppShell from "../components/app/AppShell";
import { Button } from "../components/ui/button";
import { useI18n } from "../i18n";

/**
 * RC-WEB-6 PHASE 1/10 — the real `Protecciones` section. `/dashboard/vehicles`
 * used to redirect to `/dashboard`, which is why the sidebar kept highlighting
 * "Resumen" after clicking "Protecciones".
 */

const TYPE_ICON = { vehicle: Car, kid: Baby, pet: Dog, object: Briefcase, other: Star };
const TYPE_TINT = {
  vehicle: "bg-[#1B84FF]/10 text-[#1B84FF]",
  kid: "bg-rose-100 text-rose-600",
  pet: "bg-amber-100 text-amber-700",
  object: "bg-indigo-100 text-indigo-600",
  other: "bg-teal-100 text-teal-700",
};

export const protectionState = (p) => {
  if (p?.qr_status === "paused") return "paused";
  if (p?.qr_status === "active" || p?.payment_status === "approved") return "active";
  if (p?.payment_status === "payment_review") return "review";
  return "pending";
};

export default function Protections() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    axios.get("/api/vehicles")
      .then((r) => { if (alive) setItems(Array.isArray(r.data) ? r.data : []); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const activeCount = items.filter((p) => protectionState(p) === "active").length;

  const badge = (state) => {
    const cfg = {
      active: { cls: "border-emerald-300 bg-emerald-50 text-emerald-800", label: t("app.protections.status_active"), Icon: ShieldCheck },
      review: { cls: "border-amber-300 bg-amber-50 text-amber-900", label: t("app.protections.status_review"), Icon: Clock },
      pending: { cls: "border-slate-300 bg-slate-100 text-slate-700", label: t("app.protections.status_pending"), Icon: Clock },
      paused: { cls: "border-slate-300 bg-slate-100 text-slate-700", label: t("app.protections.status_paused"), Icon: PauseCircle },
    }[state];
    return (
      <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${cfg.cls}`}>
        <cfg.Icon className="h-3 w-3" /> {cfg.label}
      </span>
    );
  };

  return (
    <AppShell
      title={t("app.protections.title")}
      subtitle={t("app.protections.subtitle")}
      maxWidth="max-w-6xl"
      backTo="/dashboard"
      actions={
        <Link to="/new-protection">
          <Button className="rounded-full bg-[#1B84FF] font-semibold text-white hover:bg-[#0F6FDB]" data-testid="protections-new-btn">
            <Plus className="mr-1.5 h-4 w-4" /> {t("app.protections.new")}
          </Button>
        </Link>
      }
    >
      <div data-testid="protections-page">
        {loading ? (
          <div className="flex justify-center py-20" data-testid="protections-loading">
            <Loader2 className="h-6 w-6 animate-spin text-[#1B84FF]" />
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="protections-empty">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#1B84FF]/10">
              <ShieldCheck className="h-8 w-8 text-[#1B84FF]" />
            </div>
            <h3 className="mb-2 text-lg font-bold text-[#0B2545]">{t("app.protections.empty_title")}</h3>
            <p className="mx-auto mb-6 max-w-md text-sm text-slate-600">{t("app.protections.empty_text")}</p>
            <Link to="/new-protection">
              <Button className="rounded-full bg-[#1B84FF] font-semibold text-white hover:bg-[#0F6FDB]" data-testid="protections-empty-new-btn">
                <Plus className="mr-1.5 h-4 w-4" /> {t("app.protections.new")}
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <p className="mb-5 text-sm font-medium text-slate-600" data-testid="protections-count">
              {items.length === 1 ? t("app.protections.count_one") : t("app.protections.count_many", { n: items.length })} ·{" "}
              {t("app.protections.active_of", { active: activeCount, total: items.length })}
            </p>
            <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="protections-list">
              {items.map((p) => {
                const type = p.protection_type || "vehicle";
                const Icon = TYPE_ICON[type] || Car;
                const state = protectionState(p);
                return (
                  <li
                    key={p.id}
                    className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5 transition-all hover:-translate-y-0.5 hover:shadow-lg"
                    data-testid={`protection-card-${p.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${TYPE_TINT[type] || TYPE_TINT.other}`}>
                        <Icon className="h-5 w-5" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-bold text-[#0B2545]" data-testid={`protection-name-${p.id}`}>{p.name}</p>
                        <p className="text-xs text-slate-600">
                          {t(`app.protections.type_${type}`)}
                          {p.plate_number ? ` · ${t("app.protections.plate")} ${p.plate_number}` : ""}
                        </p>
                      </div>
                    </div>

                    <div className="mt-4" data-testid={`protection-status-${p.id}`}>{badge(state)}</div>

                    <div className="mt-5 flex flex-wrap gap-2">
                      {state === "pending" || state === "review" ? (
                        <Link to={`/checkout/${p.id}`} className="flex-1">
                          <Button
                            className="w-full rounded-full bg-[#1B84FF] font-semibold text-white hover:bg-[#0F6FDB]"
                            data-testid={`protection-activate-${p.id}`}
                          >
                            <CreditCard className="mr-1.5 h-4 w-4" /> {t("app.protections.activate")}
                          </Button>
                        </Link>
                      ) : (
                        <Link to={`/vehicle/${p.id}?tab=sticker`} className="flex-1">
                          <Button
                            variant="outline"
                            className="w-full rounded-full border-slate-300 font-semibold text-[#0B2545]"
                            data-testid={`protection-sticker-${p.id}`}
                          >
                            <Sticker className="mr-1.5 h-4 w-4" /> {t("app.protections.sticker")}
                          </Button>
                        </Link>
                      )}
                      <Link to={`/vehicle/${p.id}`}>
                        <Button
                          variant="ghost"
                          className="rounded-full font-semibold text-slate-700 hover:bg-slate-100"
                          data-testid={`protection-view-${p.id}`}
                        >
                          <Eye className="mr-1.5 h-4 w-4" /> {t("app.protections.view")}
                        </Button>
                      </Link>
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </AppShell>
  );
}
